import type { PipelineStatus } from "@Simplicity-Education/core";
import { worstPipelineStatus } from "@Simplicity-Education/core";
// Pure helpers for PipelineDag.tsx, extracted so the component module satisfies
// `react-refresh/only-export-components` (a Vite HMR requirement) and so tests can target the maths
// without rendering React Flow. SIM-1350 layout v2: each visible wave is a React Flow group node
// whose table cards live inside it via `parentId` and parent-relative positions;
// bronze/silver-latest pairs render as one combined card and default to collapsed; edges between
// collapsed-wave children reroute to the parent group so inter-wave connectivity stays visible.

import { MarkerType, type Edge, type Node } from "@xyflow/react";
import type {
  BronzeSilverSideMeta,
  PipelineGraph,
  Wave,
  WaveNode,
} from "../../lib/buildPipelineGraph";

export const WAVE_LABELS: Record<Wave, string> = {
  connector: "Connector",
  "bronze-silver-latest": "Bronze / Silver-latest",
  silver: "Silver",
  gold: "Gold",
  diamond: "Diamond",
  archive: "Archive",
};

/**
 * Default expansion state per wave (SIM-1350). Round 4: bronze/silver moved
 * to expanded by default — defaulting it collapsed felt inconsistent with
 * every other wave. Empty set means everything is expanded; flip a wave into
 * this set if you want it hidden by default.
 */
export const DEFAULT_COLLAPSED_WAVES: ReadonlySet<Wave> = new Set();

const WAVE_COLUMN_WIDTH = 360;
// Round 4: wider horizontal and vertical gaps so arrows have more breathing
// room and are easier to follow across waves.
const WAVE_COLUMN_GAP = 96;
const WAVE_HEADER_HEIGHT = 56;
const CHILD_HORIZONTAL_PADDING = 14;
const CHILD_VERTICAL_GAP = 16;
const CHILD_BOTTOM_PADDING = 18;

// Per-kind child heights. Combined bronze-silver cards are taller because
// they show last-updated + counts in addition to label + status.
const TABLE_CARD_HEIGHT = 64;
const COMBINED_CARD_HEIGHT = 92;

/**
 * Reduce per-node statuses to one wave-level status for the group header. SIM-2913 slice C: this
 * used to carry its own four-word vocabulary, a tenth spelling of the same question, compared
 * against a `"stale"` that no longer exists. It now returns the shared `PipelineStatus`, picking
 * the worst child by `PIPELINE_STATUS_SEVERITY` — deliberately not the enum's precedence order.
 */
function aggregateWaveStatus(nodes: WaveNode[]): PipelineStatus {
  return worstPipelineStatus(nodes.map((n) => n.status));
}

export interface WaveGroupData extends Record<string, unknown> {
  kind: "wave-group";
  wave: Wave;
  label: string;
  /** Total count of children regardless of active state. */
  totalCount: number;
  /** Number of children whose `active !== false`. Drives the "N active" header copy. */
  activeCount: number;
  status: PipelineStatus;
  expanded: boolean;
  /** Per-wave show-inactive toggle state; consumed by the WaveGroupNode header button. */
  showInactive: boolean;
}

export interface TableNodeData extends Record<string, unknown> {
  kind: "table";
  wave: Wave;
  /** Underlying entity id — TD/ED/Connector.id. */
  entityId: string;
  label: string;
  /** All cadences this entity is scheduled under. Empty when not scheduled. */
  cadences: string[];
  rowCount?: number;
  status: WaveNode["status"];
  /** Graph-node ids this table consumes; powers per-table edges + cascade tracing. */
  dependsOnIds: string[];
  /** Active flag mirrored from WaveNode. Inactive nodes render muted. */
  active: boolean;
}

export interface CombinedBronzeSilverData extends Record<string, unknown> {
  kind: "combined-bronze-silver";
  wave: "bronze-silver-latest";
  /** Pair entity id — `${prefix}|${baseName}`. */
  entityId: string;
  /** Base name only, no prefix/suffix. */
  label: string;
  /** All cadences this pair is scheduled under (e.g. ["hourly", "daily"]). */
  cadences: string[];
  status: WaveNode["status"];
  /** Epoch ms of the silver-latest (or bronze fallback) last write. Null when unknown. */
  lastModifiedMs: number | null;
  /** Number of snapshots in bronze. Null when bronze side is missing. */
  numSnapshots: number | null;
  /** Current row count in silver-latest. Null when silver side is missing. */
  numRows: number | null;
  /** Drawer surfaces these so users see what's actually in BQ. */
  bronzeTableId: string | null;
  silverLatestTableId: string | null;
  bronzeDatasetId: string | null;
  silverLatestDatasetId: string | null;
  dependsOnIds: string[];
  /** Active flag mirrored from WaveNode. Inactive nodes render muted. */
  active: boolean;
}

export interface BuildFlowResult {
  nodes: Node[];
  edges: Edge[];
  /** Width of the full canvas — used by the page container. */
  totalWidth: number;
  /** Tallest column height — drives the container height. */
  maxColumnHeight: number;
}

/**
 * Build React Flow nodes + edges for the pipeline DAG. Each visible wave produces one parent group
 * node; a wave in `expandedWaves` emits its children with `parentId` + `extent: "parent"` so React
 * Flow nests them. A collapsed wave omits its children, and any inter-wave edge that would
 * terminate at one of them reroutes to the parent group.
 */
function makeWaveGroupNode(
  wave: Wave,
  visibleChildren: WaveNode[],
  totalCount: number,
  activeCount: number,
  showInactive: boolean,
  columnIndex: number,
  expanded: boolean,
): Node {
  const x = columnIndex * (WAVE_COLUMN_WIDTH + WAVE_COLUMN_GAP);
  const groupHeight = computeGroupHeight(wave, visibleChildren.length, expanded);
  // Aggregate status is computed over the children rendered for status
  // signal. Use the full set so the header dot doesn't go green when only
  // inactive errors remain hidden.
  const status = aggregateWaveStatus(visibleChildren);
  const data: WaveGroupData = {
    kind: "wave-group",
    wave,
    label: WAVE_LABELS[wave],
    totalCount,
    activeCount,
    status,
    expanded,
    showInactive,
  };
  return {
    id: `wave:${wave}`,
    type: "wave-group",
    position: { x, y: 0 },
    data,
    width: WAVE_COLUMN_WIDTH,
    height: groupHeight,
    draggable: false,
    selectable: false,
    // SIM-1350 follow-up: parent groups must render BENEATH the edge SVG
    // layer. React Flow's default node zIndex (~1000) puts the parent's
    // bg-muted/30 surface above edges, which hides any edge whose endpoint
    // lands inside the parent's bounding box (e.g. connector → bronze parent
    // when bronze is collapsed). Setting zIndex: -1 forces wave-groups below
    // both edges (zIndex 0) and child cards (default 1000).
    zIndex: -1,
    // `style` width/height required so React Flow allocates the canvas region
    // even before children measure themselves.
    style: { width: WAVE_COLUMN_WIDTH, height: groupHeight },
  };
}

function bronzeFields(meta: BronzeSilverSideMeta | null | undefined): {
  numSnapshots: number | null;
  bronzeTableId: string | null;
  bronzeDatasetId: string | null;
} {
  if (meta == null) return { numSnapshots: null, bronzeTableId: null, bronzeDatasetId: null };
  return {
    numSnapshots: meta.numRows,
    bronzeTableId: meta.tableId,
    bronzeDatasetId: meta.datasetId,
  };
}

function silverFields(meta: BronzeSilverSideMeta | null | undefined): {
  numRows: number | null;
  silverLatestTableId: string | null;
  silverLatestDatasetId: string | null;
} {
  if (meta == null)
    return { numRows: null, silverLatestTableId: null, silverLatestDatasetId: null };
  return {
    numRows: meta.numRows,
    silverLatestTableId: meta.tableId,
    silverLatestDatasetId: meta.datasetId,
  };
}

function pickLastModified(child: WaveNode): number | null {
  const silver = child.silverLatestMeta?.lastModifiedMs;
  if (silver != null) return silver;
  return child.bronzeMeta?.lastModifiedMs ?? null;
}

function combinedBronzeSilverData(child: WaveNode): CombinedBronzeSilverData {
  return {
    kind: "combined-bronze-silver",
    wave: "bronze-silver-latest",
    entityId: child.entityId,
    label: child.label,
    cadences: child.cadences ?? [],
    status: child.status,
    lastModifiedMs: pickLastModified(child),
    ...bronzeFields(child.bronzeMeta),
    ...silverFields(child.silverLatestMeta),
    dependsOnIds: child.dependsOnIds,
    active: child.active !== false,
  };
}

function makeCombinedBronzeSilverNode(child: WaveNode, childIdx: number): Node {
  const childHeight = COMBINED_CARD_HEIGHT;
  const childY = WAVE_HEADER_HEIGHT + childIdx * (childHeight + CHILD_VERTICAL_GAP);
  return {
    id: child.id,
    type: "combined-bronze-silver",
    parentId: `wave:${child.wave}`,
    extent: "parent",
    position: { x: CHILD_HORIZONTAL_PADDING, y: childY },
    data: combinedBronzeSilverData(child),
    width: WAVE_COLUMN_WIDTH - CHILD_HORIZONTAL_PADDING * 2,
    height: childHeight,
    draggable: false,
    selectable: false,
  };
}

function makeTableCardNode(child: WaveNode, childIdx: number): Node {
  const childHeight = TABLE_CARD_HEIGHT;
  const childY = WAVE_HEADER_HEIGHT + childIdx * (childHeight + CHILD_VERTICAL_GAP);
  const data: TableNodeData = {
    kind: "table",
    wave: child.wave,
    entityId: child.entityId,
    label: child.label,
    cadences: child.cadences ?? [],
    rowCount: child.rowCount,
    status: child.status,
    dependsOnIds: child.dependsOnIds,
    active: child.active !== false,
  };
  return {
    id: child.id,
    type: "table",
    parentId: `wave:${child.wave}`,
    extent: "parent",
    position: { x: CHILD_HORIZONTAL_PADDING, y: childY },
    data,
    width: WAVE_COLUMN_WIDTH - CHILD_HORIZONTAL_PADDING * 2,
    height: childHeight,
    draggable: false,
    selectable: false,
  };
}

function makeChildNode(child: WaveNode, childIdx: number): Node {
  return child.wave === "bronze-silver-latest"
    ? makeCombinedBronzeSilverNode(child, childIdx)
    : makeTableCardNode(child, childIdx);
}

/**
 * @param showInactiveByWave per-wave override: when a wave is in this set,
 *   inactive children render alongside active. Default (empty set) hides
 *   inactive children behind the toggle in each wave header.
 */
export function buildFlowElements(
  graph: PipelineGraph,
  expandedWaves: ReadonlySet<Wave>,
  showInactiveByWave: ReadonlySet<Wave> = new Set(),
): BuildFlowResult {
  const nodes: Node[] = [];
  let maxColumnHeight = 0;

  graph.visibleWaves.forEach((wave, idx) => {
    const expanded = expandedWaves.has(wave);
    const allChildren = graph.waves[wave];
    const activeCount = allChildren.filter((c) => c.active !== false).length;
    const showInactive = showInactiveByWave.has(wave);
    const visibleChildren = showInactive
      ? allChildren
      : allChildren.filter((c) => c.active !== false);

    const groupNode = makeWaveGroupNode(
      wave,
      visibleChildren,
      allChildren.length,
      activeCount,
      showInactive,
      idx,
      expanded,
    );
    nodes.push(groupNode);
    if ((groupNode.height ?? 0) > maxColumnHeight) maxColumnHeight = groupNode.height ?? 0;

    if (!expanded) return;
    for (let childIdx = 0; childIdx < visibleChildren.length; childIdx++) {
      nodes.push(makeChildNode(visibleChildren[childIdx], childIdx));
    }
  });

  const edges = buildEdges(graph, expandedWaves, nodes);
  const totalWidth = graph.visibleWaves.length * (WAVE_COLUMN_WIDTH + WAVE_COLUMN_GAP);
  return { nodes, edges, totalWidth, maxColumnHeight };
}

function childCardHeight(wave: Wave): number {
  return wave === "bronze-silver-latest" ? COMBINED_CARD_HEIGHT : TABLE_CARD_HEIGHT;
}

function computeGroupHeight(wave: Wave, childCount: number, expanded: boolean): number {
  if (!expanded || childCount === 0) return WAVE_HEADER_HEIGHT + CHILD_BOTTOM_PADDING;
  const cardHeight = childCardHeight(wave);
  const childrenHeight = childCount * cardHeight + (childCount - 1) * CHILD_VERTICAL_GAP;
  return WAVE_HEADER_HEIGHT + childrenHeight + CHILD_BOTTOM_PADDING;
}

interface EdgeAccumulator {
  edges: Edge[];
  seen: Set<string>;
}

function pushEdge(acc: EdgeAccumulator, sourceId: string, targetId: string): void {
  if (sourceId === targetId) return;
  const key = `${sourceId}->${targetId}`;
  if (acc.seen.has(key)) return;
  acc.seen.add(key);
  acc.edges.push({
    id: `edge:${key}`,
    source: sourceId,
    target: targetId,
    markerEnd: { type: MarkerType.ArrowClosed },
    animated: false,
  });
}

function endpointForChild(child: WaveNode, expandedWaves: ReadonlySet<Wave>): string {
  return expandedWaves.has(child.wave) ? child.id : `wave:${child.wave}`;
}

function buildPerTableEdges(
  graph: PipelineGraph,
  expandedWaves: ReadonlySet<Wave>,
  acc: EdgeAccumulator,
  childById: Map<string, WaveNode>,
  emittedNodeIds: Set<string>,
): void {
  for (const wave of graph.visibleWaves) {
    for (const child of graph.waves[wave]) {
      for (const depId of child.dependsOnIds) {
        const dep = childById.get(depId);
        if (dep == null) continue;
        const sourceId = endpointForChild(dep, expandedWaves);
        const targetId = endpointForChild(child, expandedWaves);
        if (!emittedNodeIds.has(sourceId) || !emittedNodeIds.has(targetId)) continue;
        pushEdge(acc, sourceId, targetId);
      }
    }
  }
}

function buildWaveContinuityEdges(
  graph: PipelineGraph,
  expandedWaves: ReadonlySet<Wave>,
  acc: EdgeAccumulator,
): void {
  // Coarse wave-to-wave continuity edge between adjacent visible waves that
  // don't already have a per-table edge connecting them. Emitted only when
  // both ends are collapsed; expanded waves are connected via per-table edges.
  for (let i = 0; i < graph.visibleWaves.length - 1; i++) {
    const from = graph.visibleWaves[i];
    const to = graph.visibleWaves[i + 1];
    if (expandedWaves.has(from) || expandedWaves.has(to)) continue;
    pushEdge(acc, `wave:${from}`, `wave:${to}`);
  }
}

/**
 * Build the edge list, rerouting endpoints to the parent group when a child's
 * wave is collapsed. Deduplicates wave→wave edges that would otherwise emit
 * multiple times for the same pair.
 */
function buildEdges(graph: PipelineGraph, expandedWaves: ReadonlySet<Wave>, nodes: Node[]): Edge[] {
  const childById = new Map<string, WaveNode>();
  for (const wave of graph.visibleWaves) {
    for (const child of graph.waves[wave]) {
      childById.set(child.id, child);
    }
  }
  const emittedNodeIds = new Set(nodes.map((n) => n.id));
  const acc: EdgeAccumulator = { edges: [], seen: new Set() };
  buildPerTableEdges(graph, expandedWaves, acc, childById, emittedNodeIds);
  buildWaveContinuityEdges(graph, expandedWaves, acc);
  return acc.edges;
}

/**
 * Strict directional cascade (SIM-1350). Returns the seed plus its strict upstream ancestors and
 * strict downstream descendants. The two walks deliberately do not cross-pollinate: walking
 * upstream never pivots to walk downstream from an ancestor, which keeps sibling subtrees out —
 * clicking `attendance` bronze must not highlight `students` bronze for a shared connector.
 */
export function computeCascade(seedId: string, edges: Edge[]): Set<string> {
  const downstream = new Map<string, string[]>();
  const upstream = new Map<string, string[]>();
  for (const e of edges) {
    const dList = downstream.get(e.source);
    if (dList != null) dList.push(e.target);
    else downstream.set(e.source, [e.target]);
    const uList = upstream.get(e.target);
    if (uList != null) uList.push(e.source);
    else upstream.set(e.target, [e.source]);
  }
  const result = new Set<string>([seedId]);
  walkOneDirection(seedId, upstream, result);
  walkOneDirection(seedId, downstream, result);
  return result;
}

function walkOneDirection(
  seedId: string,
  adjacency: Map<string, string[]>,
  result: Set<string>,
): void {
  const queue: string[] = [seedId];
  while (queue.length > 0) {
    const cur = queue.shift() as string;
    for (const next of adjacency.get(cur) ?? []) {
      if (!result.has(next)) {
        result.add(next);
        queue.push(next);
      }
    }
  }
}

/**
 * Map a {wave, entityId} selection (the public surface used by the drawer)
 * to its synthetic React Flow node id. Inverse of the id format that
 * buildPipelineGraph emits.
 */
export function selectionToNodeId(selection: { wave: Wave; entityId: string }): string {
  switch (selection.wave) {
    case "connector":
      return `connector:${selection.entityId}`;
    case "bronze-silver-latest":
      // entityId is already `${prefix}|${baseName}`.
      return `bronze-silver-latest:${selection.entityId}`;
    case "silver":
    case "gold":
    case "diamond":
    case "archive":
      return `${selection.wave}:${selection.entityId}`;
  }
}
